/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable functional/immutable-data */
/* eslint-disable prefer-const */
/* eslint-disable new-cap */
import { createState, useState } from '@hookstate/core'
import { Broadcasted } from '@hookstate/broadcasted'
import type { IListDevice } from '../interfaces/IListDevice'
import type {
  updateCurrentDispatchDeviceTableProps,
  updateDeviceGroupChangeProps,
  UpdateDeviceRowsTableProps,
  updateNewDeviceCreatedProps,
  updateStatusDeviceRowsTableProps,
  updateTransferDeviceRowsTableProps,
  UpdateTypeAndSubtypeRowsTableProps,
} from '../interfaces/IListDeviceTab'

export interface IDeviceRowsTable {
  available: IListDevice[]
  dispatched: IListDevice[]
  disconnected: IListDevice[]
  'not-in-service': IListDevice[]
  'on-mission': IListDevice[]
}

export const deviceRowsTableDefault = {
  available: [],
  dispatched: [],
  disconnected: [],
  'not-in-service': [],
  'on-mission': [],
}

const deviceRowsTable = createState<IDeviceRowsTable>(deviceRowsTableDefault)
const deviceRowsTableFiltered = createState<IDeviceRowsTable>(
  deviceRowsTableDefault
)
const triggerNewSocket = createState(false)

// Adiciona Broadcasted para sincronização entre componentes
deviceRowsTable.attach(Broadcasted('device-rows-table'))
deviceRowsTableFiltered.attach(Broadcasted('device-rows-table-filtered'))
triggerNewSocket.attach(Broadcasted('device-trigger-socket'))

// eslint-disable-next-line max-lines-per-function
export default function useDeviceTableState() {
  const deviceRowsTableState = useState(deviceRowsTable)
  const deviceRowsTableFilteredState = useState(deviceRowsTableFiltered)
  const triggerNewSocketState = useState(triggerNewSocket)

  return {
    deviceRowsTableState: () => deviceRowsTableState,
    deviceRowsTableStateFiltered: () => deviceRowsTableFilteredState,
    triggerNewSocketState: () => triggerNewSocketState,
    setDeviceRowsTableDefault: () => {
      deviceRowsTable.set({
        available: [],
        dispatched: [],
        disconnected: [],
        'not-in-service': [],
        'on-mission': [],
      })
    },
    updateStatusDeviceRowsTable: ({
      deviceId,
      deviceName,
      dispatchGroup,
      dispatch,
      outOfService,
      currentStatus,
      deviceLog,
      onMission,
    }: updateStatusDeviceRowsTableProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])
        if (deviceId) {
          const devicesUpdate = allDevices.filter(
            (row) => row['id'] === deviceId
          )
          const devicesSameDispatch = dispatch
            ? allDevices.filter(
                (row) =>
                  row['dispatchId'] ===
                    (dispatch?.id ? dispatch?.id : dispatch) &&
                  row['id'] != deviceId
              )
            : [] // devices associados ao mesmo despacho

          const deviceIndexUpdate = allDevices.findIndex(
            (row) => row['id'] === devicesUpdate[0]['id']
          )

          let currentDeviceUpdate = JSON.parse(
            JSON.stringify(allDevices[deviceIndexUpdate])
          )

          // Preserva o removalRequested existente se estiver definido
          const existingRemovalRequested = currentDeviceUpdate['removalRequested']

          const commanderUpdated = deviceLog?.crew?.find(
            (item) => item.position.id == 1
          )?.user.representation_name

          currentDeviceUpdate['identification'] = deviceName

          if (currentStatus) {
            currentDeviceUpdate['statusId'] = currentStatus.id
            currentDeviceUpdate['status'] = currentStatus.acronym
          }

          if (dispatchGroup) {
            currentDeviceUpdate['dispatchGroupId'] = dispatchGroup.id
            currentDeviceUpdate['dispatchGroup'] = dispatchGroup.name
          }

          if (dispatch) {
            currentDeviceUpdate['dispatchStatus'] = dispatch?.status_id
            currentDeviceUpdate['dispatchId'] = dispatch?.id
            currentDeviceUpdate['dispatch'] = dispatch?.code
            currentDeviceUpdate['statusChangedAt'] = dispatch?.status_changed_at
            currentDeviceUpdate['occurrenceId'] = dispatch?.occurrence.id
            currentDeviceUpdate[
              'type'
            ] = `[${dispatch.type.code}] ${dispatch.type.name}`
            currentDeviceUpdate['subtype'] = dispatch.subtype
              ? `[${dispatch.subtype.code}] ${dispatch.subtype.name}`
              : '-'
          }

          if (outOfService) {
            currentDeviceUpdate['outOfServiceDate'] =
              outOfService?.outOfServiceDate
            currentDeviceUpdate['outOfServiceReason'] =
              outOfService?.reason.name
            currentDeviceUpdate['comment'] = outOfService?.comment
          }

          if (deviceLog) {
            currentDeviceUpdate['task'] = deviceLog.task
            currentDeviceUpdate['notes'] =
              currentStatus?.id === 10 ? null : deviceLog.notes
            currentDeviceUpdate['phone'] = deviceLog.phone
            currentDeviceUpdate['availableDate'] = deviceLog.date_connected
            currentDeviceUpdate['disconnectedDate'] =
              deviceLog.date_disconnected
            currentDeviceUpdate['commander'] = commanderUpdated
          }

          if (onMission) {
            currentDeviceUpdate['comment'] = onMission?.comment
            currentDeviceUpdate['onMissionDate'] = onMission?.on_mission_date
            currentDeviceUpdate['onMissionReason'] = onMission?.reason.name
          }

          // Restaura o removalRequested se não foi definido/alterado nesta atualização
          if (existingRemovalRequested !== undefined && existingRemovalRequested !== null) {
            currentDeviceUpdate['removalRequested'] = existingRemovalRequested
          }

          allDevices[deviceIndexUpdate] = currentDeviceUpdate

          if (devicesSameDispatch.length > 0) {
            for (const devices of devicesSameDispatch) {
              const deviceIndexUpdate = allDevices.findIndex(
                (row) => row['id'] === devices['id']
              )

              let currentDeviceUpdate = JSON.parse(
                JSON.stringify(allDevices[deviceIndexUpdate])
              )

              currentDeviceUpdate['dispatchStatus'] = dispatch?.status_id

              allDevices[deviceIndexUpdate] = currentDeviceUpdate
            }
          }
        }

        deviceRowsTableState.set({
          available: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
          ),
          dispatched: JSON.parse(
            JSON.stringify(
              allDevices.filter(
                (row) => row['statusId'] >= 4 && row['statusId'] <= 9
              )
            )
          ),
          disconnected: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
          ),
          'not-in-service': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
          ),
          'on-mission': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
          ),
        })

        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateDeviceTypeAndSubtypeRowsTable: ({
      dispatchId,
      type,
      subtype,
    }: UpdateTypeAndSubtypeRowsTableProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])

        const devicesSameDispatch = allDevices.filter(
          (row) => row['dispatchId'] === dispatchId
        )

        if (devicesSameDispatch.length > 0) {
          for (const devices of devicesSameDispatch) {
            const deviceIndexUpdate = allDevices.findIndex(
              (row) =>
                row['dispatchId'] === devices['dispatchId'] &&
                row['id'] === devices.id
            )

            if (deviceIndexUpdate || deviceIndexUpdate == 0) {
              let currentDeviceUpdate = JSON.parse(
                JSON.stringify(allDevices[deviceIndexUpdate])
              )

              currentDeviceUpdate['type'] = type
              currentDeviceUpdate['subtype'] = subtype

              allDevices[deviceIndexUpdate] = currentDeviceUpdate
            }
          }
          deviceRowsTableState.set({
            available: JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
            ),
            dispatched: JSON.parse(
              JSON.stringify(
                allDevices.filter(
                  (row) => row['statusId'] >= 4 && row['statusId'] <= 9
                )
              )
            ),
            disconnected: JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
            ),
            'not-in-service': JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
            ),
            'on-mission': JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
            ),
          })
        }

        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateCurrentDispatchDeviceTable: ({
      dispatchId,
      currentDispatchStatus,
    }: updateCurrentDispatchDeviceTableProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])

        const devicesSameDispatch = allDevices.filter(
          (row) => row['dispatchId'] === dispatchId
        )

        if (devicesSameDispatch.length > 0) {
          for (const devices of devicesSameDispatch) {
            const deviceIndexUpdate = allDevices.findIndex(
              (row) =>
                row['dispatchId'] === devices['dispatchId'] &&
                row['id'] === devices.id
            )

            if (deviceIndexUpdate || deviceIndexUpdate == 0) {
              let currentDeviceUpdate = JSON.parse(
                JSON.stringify(allDevices[deviceIndexUpdate])
              )

              currentDeviceUpdate['dispatchStatus'] = currentDispatchStatus

              allDevices[deviceIndexUpdate] = currentDeviceUpdate
            }
          }
          deviceRowsTableState.set({
            available: JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
            ),
            dispatched: JSON.parse(
              JSON.stringify(
                allDevices.filter(
                  (row) => row['statusId'] >= 4 && row['statusId'] <= 9
                )
              )
            ),
            disconnected: JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
            ),
            'not-in-service': JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
            ),
            'on-mission': JSON.parse(
              JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
            ),
          })
        }

        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateTransferDeviceRowsTable: ({
      deviceId,
      destinyDispatchGroupId,
    }: updateTransferDeviceRowsTableProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])

        const devicesUpdate = allDevices.filter((row) => row['id'] === deviceId)

        const deviceIndexUpdate = allDevices.findIndex(
          (row) => row['id'] === devicesUpdate[0]['id']
        )

        let currentDeviceUpdate = JSON.parse(
          JSON.stringify(allDevices[deviceIndexUpdate])
        )

        currentDeviceUpdate['dispatchGroupId'] = destinyDispatchGroupId

        allDevices[deviceIndexUpdate] = currentDeviceUpdate

        deviceRowsTableState.set({
          available: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
          ),
          dispatched: JSON.parse(
            JSON.stringify(
              allDevices.filter(
                (row) => row['statusId'] >= 4 && row['statusId'] <= 9
              )
            )
          ),
          disconnected: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
          ),
          'not-in-service': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
          ),
          'on-mission': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
          ),
        })

        // triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateDeviceGroupChange: ({
      deviceId,
      deviceName,
      deviceLog,
      destinyDispatchGroup,
      hasAccessToDestinyGroup,
      currentStatus,
    }: updateDeviceGroupChangeProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])

        const devicesUpdate = allDevices.filter((row) => row['id'] === deviceId)

        if (!devicesUpdate.length && hasAccessToDestinyGroup) {
          const commanderUpdated = deviceLog.crew?.find(
            (item) => item.position.id == 1
          )?.user.representation_name

          let currentDeviceUpdate = JSON.parse(JSON.stringify(allDevices))

          currentDeviceUpdate['id'] = deviceId
          currentDeviceUpdate['identification'] = deviceName
          currentDeviceUpdate['task'] = deviceLog.task
          currentDeviceUpdate['notes'] = deviceLog.notes
          currentDeviceUpdate['phone'] = deviceLog.phone
          currentDeviceUpdate['availableDate'] = deviceLog.date_connected
          currentDeviceUpdate['disconnectedDate'] = deviceLog.date_disconnected
          currentDeviceUpdate['commander'] = commanderUpdated
          currentDeviceUpdate['statusId'] = currentStatus.id
          currentDeviceUpdate['status'] = currentStatus.acronym
          currentDeviceUpdate['dispatchGroupId'] = destinyDispatchGroup.id
          currentDeviceUpdate['dispatchGroup'] = destinyDispatchGroup.name

          allDevices.push({
            id: deviceId,
            identification: deviceName,
            task: deviceLog.task ?? '',
            notes: deviceLog.notes ?? '',
            phone: deviceLog.phone ?? '',
            availableDate: deviceLog.date_connected,
            disconnectedDate: deviceLog.date_disconnected ?? '',
            commander: commanderUpdated ?? '',
            statusId: currentStatus.id,
            status: currentStatus.acronym,
            dispatchGroupId: destinyDispatchGroup.id,
            dispatchGroup: destinyDispatchGroup.name,
          })
        }

        if (devicesUpdate.length && !hasAccessToDestinyGroup) {
          const deviceIndexUpdate = allDevices.findIndex(
            (row) => row['id'] === devicesUpdate[0]['id']
          )
          allDevices.splice(deviceIndexUpdate, 1)
        }

        if (devicesUpdate.length && hasAccessToDestinyGroup) {
          const deviceIndexUpdate = allDevices.findIndex(
            (row) => row['id'] === devicesUpdate[0]['id']
          )

          const commanderUpdated = deviceLog.crew?.find(
            (item) => item.position.id == 1
          )?.user.representation_name

          let currentDeviceUpdate = JSON.parse(
            JSON.stringify(allDevices[deviceIndexUpdate])
          )

          currentDeviceUpdate['identification'] = deviceName

          currentDeviceUpdate['task'] = deviceLog.task
          currentDeviceUpdate['notes'] = deviceLog.notes
          currentDeviceUpdate['phone'] = deviceLog.phone
          currentDeviceUpdate['availableDate'] = deviceLog.date_connected
          currentDeviceUpdate['disconnectedDate'] = deviceLog.date_disconnected
          currentDeviceUpdate['commander'] = commanderUpdated

          currentDeviceUpdate['dispatchGroupId'] = destinyDispatchGroup.id
          currentDeviceUpdate['dispatchGroup'] = destinyDispatchGroup.name

          allDevices[deviceIndexUpdate] = currentDeviceUpdate
        }

        deviceRowsTableState.set({
          available: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
          ),
          dispatched: JSON.parse(
            JSON.stringify(
              allDevices.filter(
                (row) => row['statusId'] >= 4 && row['statusId'] <= 9
              )
            )
          ),
          disconnected: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
          ),
          'not-in-service': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
          ),
          'on-mission': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
          ),
        })

        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateNewDeviceCreated: ({
      deviceId,
      deviceName,
      currentStatus,
      deviceLog,
      dispatchGroup,
    }: updateNewDeviceCreatedProps) => {
      try {
        const allDevices = deviceRowsTableState
          .get()
          ['available'].concat(deviceRowsTableState.get()['dispatched'])
          .concat(deviceRowsTableState.get()['disconnected'])
          .concat(deviceRowsTableState.get()['not-in-service'])
          .concat(deviceRowsTableState.get()['on-mission'])

        allDevices.push({
          id: deviceId,
          identification: deviceName,
          task: deviceLog?.task ?? '',
          notes: deviceLog?.notes ?? '',
          phone: deviceLog?.phone ?? '',
          availableDate: deviceLog?.date_connected,
          disconnectedDate: deviceLog?.date_disconnected ?? '',
          statusId: currentStatus.id,
          status: currentStatus.acronym,
          dispatchGroupId: dispatchGroup.id,
          dispatchGroup: dispatchGroup.name,
        })

        deviceRowsTableState.set({
          available: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
          ),
          dispatched: JSON.parse(
            JSON.stringify(
              allDevices.filter(
                (row) => row['statusId'] >= 4 && row['statusId'] <= 9
              )
            )
          ),
          disconnected: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
          ),
          'not-in-service': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
          ),
          'on-mission': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
          ),
        })

        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    updateDeviceRowsTable: ({
      deviceId,
      closureRequestedDevice,
      recallWasViewed,
      elapsedTime,
    }: UpdateDeviceRowsTableProps) => {
      const allDevices = deviceRowsTableState
        .get()
        ['available'].concat(deviceRowsTableState.get()['dispatched'])
        .concat(deviceRowsTableState.get()['disconnected'])
        .concat(deviceRowsTableState.get()['not-in-service'])
        .concat(deviceRowsTableState.get()['on-mission'])
      const devicesUpdate = allDevices.filter(
        (row) => String(row['id']) === String(deviceId)
      )
      
      // Se não há dispositivos no estado, retorna early para evitar erros
      if (allDevices.length === 0) {
        console.warn('Estado dos dispositivos está vazio. Tentativa de atualizar será ignorada.')
        return
      }
      
      try {
        for (const deviceUpdate of devicesUpdate) {
          const deviceIndexUpdate = allDevices.findIndex(
            (row) => row['id'] == deviceUpdate['id']
          )
          // Ajusta a condição do índice para incluir 0, como em updateOccurrenceRowsTable
          if (deviceIndexUpdate >= 0) {
            let currentDeviceUpdate = JSON.parse(
              JSON.stringify(allDevices[deviceIndexUpdate])
            )
            if (closureRequestedDevice !== undefined) {
              currentDeviceUpdate['removalRequested'] = closureRequestedDevice
            }
            if (recallWasViewed !== undefined) {
              currentDeviceUpdate['recall_was_viewed'] = recallWasViewed
            }
            if (elapsedTime) {
              currentDeviceUpdate['status_updated_at'] = elapsedTime
              currentDeviceUpdate['elapsed_time'] = elapsedTime
            }

            allDevices[deviceIndexUpdate] = currentDeviceUpdate
          }
        }
        // Aplica os estados de dispositivos, mantendo a lógica de updateOccurrenceRowsTable
        deviceRowsTableState.set({
          available: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 1))
          ),
          dispatched: JSON.parse(
            JSON.stringify(
              allDevices.filter(
                (row) => row['statusId'] >= 4 && row['statusId'] <= 9
              )
            )
          ),
          disconnected: JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 10))
          ),
          'not-in-service': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 11))
          ),
          'on-mission': JSON.parse(
            JSON.stringify(allDevices.filter((row) => row['statusId'] == 12))
          ),
        })

        // Usa triggerNewSocketState, como definido no arquivo
        triggerNewSocketState.set(!triggerNewSocketState.get())
      } catch (error) {
        throw error
      }
    },
  }
}
